import { isMiniprogram, isWechatBrowser } from '@lighthouse/tools'
import type { CarouselProps, Embla } from '@mantine/carousel'
import { Carousel, useAnimationOffsetEffect } from '@mantine/carousel'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import { isMobileDevice } from '../../utils'
import { FileItem } from './FileItem'
import type { PreviewFile } from './types'
import { shouldEnableWechatNativeImageMenu } from './wechatImageLongPress'
import { WechatNativeImageGallery } from './WechatNativeImageGallery'

const StyledCarousel = styled(Carousel)`
    .mantine-Carousel-slide {
        height: 100%;
        overflow: hidden;
        img,
        video,
        audio {
            max-width: 100%;
            max-height: 100%;
        }
    }
    .mantine-Carousel-control {
        box-shadow: var(--box-shadow);
        transition: all 150ms ease;
        svg {
            width: 32px;
            height: 32px;
        }
        &:not([data-inactive='true']):hover {
            background-color: #9e77ed;
            color: var(--color-white);
        }
    }
`

interface FileListCarouselProps extends CarouselProps {
    fileList: PreviewFile[]
    defaultIndex: number
    currentIndex: number
    onSlideChange: (i: number) => void
    disableWechatDocumentPreview?: boolean
}

// 文件轮播图
export const FileCarousel = ({
    fileList,
    defaultIndex,
    currentIndex,
    onSlideChange: propOnSlideChange,
    disableWechatDocumentPreview,
    ...rest
}: FileListCarouselProps) => {
    const [embla, setEmbla] = useState<Embla | null>(null)
    useAnimationOffsetEffect(embla, 200)

    useEffect(() => {
        embla?.scrollTo(defaultIndex, true)
    }, [defaultIndex, embla])

    const onSlideChange = useCallback(
        (i: number) => {
            propOnSlideChange(i)
        },
        [propOnSlideChange]
    )

    const isMobile = isMobileDevice()
    const wechatEnv = useMemo(
        () => ({
            isMobile,
            isWechatBrowser: isWechatBrowser(),
            isMiniprogram: isMiniprogram()
        }),
        [isMobile]
    )
    const useWechatNativeGallery = fileList.length > 0 && fileList.every(file => shouldEnableWechatNativeImageMenu(file, wechatEnv))

    if (useWechatNativeGallery) {
        return <WechatNativeImageGallery defaultIndex={defaultIndex} fileList={fileList} onSlideChange={onSlideChange} />
    }

    return (
        <StyledCarousel
            height="100%"
            sx={{ height: '100%' }}
            getEmblaApi={setEmbla}
            controlsOffset={isMobile ? 20 : 24}
            controlSize={41}
            onSlideChange={onSlideChange}
            {...rest}
        >
            {fileList.map((item, index) => (
                <FileItem
                    key={item.url || index} // 使用文件URL作为key，避免index警告
                    file={item}
                    showPdfThumbs={!isMobile}
                    active={index === currentIndex}
                    disableWechatDocumentPreview={disableWechatDocumentPreview}
                />
            ))}
        </StyledCarousel>
    )
}
